import { useEffect, useState } from 'react';
import { create } from 'zustand';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Button } from '../Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '../Dialog/Dialog';
import { Field } from '../Field/Field';
import { Input, Textarea } from '../Input';
import { Scroller } from '@/shared/ui/Scroller';
import styles from './ConfirmDialog.module.css';

/**
 * Императивная замена window.confirm/window.prompt в стиле Luma.
 *
 *   if (!(await confirmDialog({ title: 'Удалить категорию?', danger: true }))) return;
 *   const name = await promptDialog({ title: 'Новый каталог', label: 'Название' }); // null — отмена
 *
 * Хост <ConfirmDialogHost /> монтируется один раз в AppProviders.
 *
 * @use спросить подтверждение или короткий ввод прямо из кода: `confirmDialog` вместо window.confirm, `promptDialog` вместо window.prompt.
 */

export interface ConfirmDialogOptions {
  title: string;
  description?: string;
  items?: string[];
  code?: string;
  Icon?: LucideIcon;
  tone?: 'warning' | 'danger' | 'info';
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export interface PromptDialogOptions {
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  multiline?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface PendingRequest {
  options: ConfirmDialogOptions & Omit<PromptDialogOptions, 'title'> & { withInput?: boolean };
  resolve: (value: never) => void;
}

interface ConfirmState {
  pending: PendingRequest | null;
  open: (req: PendingRequest) => void;
  close: () => void;
}

const useConfirmStore = create<ConfirmState>((set) => ({
  pending: null,
  open: (req) =>
    set((s) => {
      if (s.pending) (s.pending.resolve as (v: unknown) => void)(s.pending.options.withInput ? null : false);
      return { pending: req };
    }),
  close: () => set({ pending: null }),
}));

export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    useConfirmStore.getState().open({ options, resolve: resolve as PendingRequest['resolve'] });
  });
}

export function promptDialog(options: PromptDialogOptions): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    useConfirmStore.getState().open({
      options: { ...options, withInput: true },
      resolve: resolve as PendingRequest['resolve'],
    });
  });
}

export const ConfirmDialogHost: React.FC = () => {
  const pending = useConfirmStore((s) => s.pending);
  const close = useConfirmStore((s) => s.close);
  const [text, setText] = useState('');

  useEffect(() => {
    if (pending) setText(pending.options.defaultValue ?? '');
  }, [pending]);

  const finish = (confirmed: boolean) => {
    if (!pending) return;
    const resolve = pending.resolve as (v: unknown) => void;
    resolve(pending.options.withInput ? (confirmed ? text : null) : confirmed);
    close();
  };

  const opts = pending?.options;

  return (
    <Dialog open={pending != null} onOpenChange={(next) => !next && finish(false)}>
      {opts && (
        <DialogContent>
          { }
          <DialogTitle className={styles.title}>
            {opts.Icon && (
              <span
                className={cn(styles.icon, opts.tone && styles[`icon-${opts.tone}`])}
                aria-hidden
              >
                <opts.Icon size={18} />
              </span>
            )}
            {opts.title}
          </DialogTitle>
          {opts.description && <DialogDescription>{opts.description}</DialogDescription>}
          {opts.code && <p className={styles.code}>{opts.code}</p>}
          {opts.items && opts.items.length > 0 && (
            <Scroller className={styles.itemScroll}>
              <ul className={styles.itemList}>
              { }
              {opts.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            </Scroller>
          )}
          {opts.withInput && (
            <Field label={opts.label ?? 'Значение'}>
              {opts.multiline ? (
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={opts.placeholder}
                  autoFocus
                />
              ) : (
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={opts.placeholder}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      finish(true);
                    }
                  }}
                />
              )}
            </Field>
          )}
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => finish(false)}>
              {opts.cancelLabel ?? 'Отмена'}
            </Button>
            <Button
              variant={opts.danger ? 'destructive' : 'primary'}
              type="button"
              autoFocus={!opts.withInput}
              onClick={() => finish(true)}
            >
              {opts.confirmLabel ?? (opts.danger ? 'Удалить' : 'ОК')}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};
